import { createBrowserRouter, Navigate } from "react-router-dom";

import { AppShellLayout } from "@/app/routes/AppShellLayout";
import { ErrorPage } from "@/app/routes/ErrorPage";
import { ProtectedRoute } from "@/app/routes/ProtectedRoute";
import { PublicLayout } from "@/app/routes/PublicLayout";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { PermissionRoute } from "@/features/auth/components/PermissionGate";
import { DashboardOverviewRoute } from "@/features/dashboard/routes/DashboardOverviewRoute";
import { ForgotPasswordRoute } from "@/features/auth/routes/ForgotPasswordRoute";
import { LoginRoute } from "@/features/auth/routes/LoginRoute";
import { DriversRoute } from "@/features/drivers/routes/DriversRoute";
import { DriverDetailsRoute } from "@/features/drivers/routes/DriverDetailsRoute";
import { RidersRoute } from "@/features/riders/routes/RidersRoute";
import { RiderDetailsRoute } from "@/features/riders/routes/RiderDetailsRoute";
import { UsersRoute } from "@/features/users/routes/UsersRoute";
import { PlatformConfigurationRoute } from "@/features/platform-configuration/routes/PlatformConfigurationRoute";
import { AdminsRoute } from "@/features/admin-management/routes/AdminsRoute";
import { AdminDetailsRoute } from "@/features/admin-management/routes/AdminDetailsRoute";
import { CreateAdminRoute } from "@/features/admin-management/routes/CreateAdminRoute";
import { EditAdminRoute } from "@/features/admin-management/routes/EditAdminRoute";
import { SettingsRoute } from "@/features/settings/routes/SettingsRoute";
import { RideBookingsTripsLayout } from "@/features/ride-bookings-trips/routes/RideBookingsTripsLayout";
import { RideOffersRoute } from "@/features/ride-bookings-trips/routes/RideOffersRoute";
import { RideOfferDetailsRoute } from "@/features/ride-bookings-trips/routes/RideOfferDetailsRoute";
import { BookingsRoute } from "@/features/ride-bookings-trips/routes/BookingsRoute";
import { BookingDetailsRoute } from "@/features/ride-bookings-trips/routes/BookingDetailsRoute";
import { TripsRoute } from "@/features/ride-bookings-trips/routes/TripsRoute";
import { TripDetailsRoute } from "@/features/ride-bookings-trips/routes/TripDetailsRoute";
import { WalletPaymentsLayout } from "@/features/wallet-payments/routes/WalletPaymentsLayout";
import { WalletsRoute } from "@/features/wallet-payments/routes/WalletsRoute";
import { WalletDetailsRoute } from "@/features/wallet-payments/routes/WalletDetailsRoute";
import { TransactionsRoute } from "@/features/wallet-payments/routes/TransactionsRoute";
import { TransactionDetailsRoute } from "@/features/wallet-payments/routes/TransactionDetailsRoute";
import { PaymentsRoute } from "@/features/wallet-payments/routes/PaymentsRoute";
import { PaymentDetailsRoute } from "@/features/wallet-payments/routes/PaymentDetailsRoute";
import { PlatformWalletRoute } from "@/features/wallet-payments/routes/PlatformWalletRoute";
import { FraudSafetyLayout } from "@/features/fraud-safety/routes/FraudSafetyLayout";
import { AlertsRoute } from "@/features/fraud-safety/routes/AlertsRoute";
import { AlertDetailsRoute } from "@/features/fraud-safety/routes/AlertDetailsRoute";
import { CasesRoute } from "@/features/fraud-safety/routes/CasesRoute";
import { CaseDetailsRoute } from "@/features/fraud-safety/routes/CaseDetailsRoute";
import { FraudOverviewRoute } from "@/features/fraud-safety/routes/FraudOverviewRoute";
import { LiveMonitoringLayout } from "@/features/live-monitoring/routes/LiveMonitoringLayout";
import { LiveDashboardRoute } from "@/features/live-monitoring/routes/LiveDashboardRoute";
import { LiveDriversRoute } from "@/features/live-monitoring/routes/LiveDriversRoute";
import { LiveDriverDetailsRoute } from "@/features/live-monitoring/routes/LiveDriverDetailsRoute";
import { LiveTripsRoute } from "@/features/live-monitoring/routes/LiveTripsRoute";
import { LiveTripDetailsRoute } from "@/features/live-monitoring/routes/LiveTripDetailsRoute";
import { LiveAnalyticsRoute } from "@/features/live-monitoring/routes/LiveAnalyticsRoute";
import { ReportsAnalyticsLayout } from "@/features/reports-analytics/routes/ReportsAnalyticsLayout";
import { AnalyticsSummaryRoute } from "@/features/reports-analytics/routes/AnalyticsSummaryRoute";
import { FinancialAnalyticsRoute } from "@/features/reports-analytics/routes/FinancialAnalyticsRoute";
import { RevenueAnalyticsRoute } from "@/features/reports-analytics/routes/RevenueAnalyticsRoute";
import { TripAnalyticsRoute } from "@/features/reports-analytics/routes/TripAnalyticsRoute";
import { UserAnalyticsRoute } from "@/features/reports-analytics/routes/UserAnalyticsRoute";
import { GeographicAnalyticsRoute } from "@/features/reports-analytics/routes/GeographicAnalyticsRoute";
import { OperationalAnalyticsRoute } from "@/features/reports-analytics/routes/OperationalAnalyticsRoute";
import { QualityAnalyticsRoute } from "@/features/reports-analytics/routes/QualityAnalyticsRoute";
import { NotificationsLayout } from "@/features/notifications/routes/NotificationsLayout";
import { AnnouncementsRoute } from "@/features/notifications/routes/AnnouncementsRoute";
import { AnnouncementComposerRoute } from "@/features/notifications/routes/AnnouncementComposerRoute";
import { AnnouncementDetailsRoute } from "@/features/notifications/routes/AnnouncementDetailsRoute";
import { MessagingStatsRoute } from "@/features/notifications/routes/MessagingStatsRoute";
import { AuditLogsLayout } from "@/features/audit-logs/routes/AuditLogsLayout";
import { AuditLogsListRoute } from "@/features/audit-logs/routes/AuditLogsListRoute";
import { AuditLogDetailsRoute } from "@/features/audit-logs/routes/AuditLogDetailsRoute";
import { SecurityAuditRoute } from "@/features/audit-logs/routes/SecurityAuditRoute";
import { AuditStatsRoute } from "@/features/audit-logs/routes/AuditStatsRoute";
import { MyActionsRoute } from "@/features/audit-logs/routes/MyActionsRoute";
import { ApprovalsLayout } from "@/features/approvals/routes/ApprovalsLayout";
import { ApprovalQueueRoute } from "@/features/approvals/routes/ApprovalQueueRoute";
import { ApprovalQueueDetailsRoute } from "@/features/approvals/routes/ApprovalQueueDetailsRoute";
import { DriverLicenceRoute } from "@/features/approvals/routes/DriverLicenceRoute";
import { DriverLicenceDetailsRoute } from "@/features/approvals/routes/DriverLicenceDetailsRoute";
import { RoadWorthinessRoute } from "@/features/approvals/routes/RoadWorthinessRoute";

export const router = createBrowserRouter([
    {
        path: "/auth",
        element: <PublicLayout />,
        children: [
            {
                path: "login",
                element: <LoginRoute />
            },
            {
                path: "forgot-password",
                element: <ForgotPasswordRoute />
            }
        ]
    },
    {
        path: "/",
        element: (
            <ProtectedRoute>
                <AppShellLayout />
            </ProtectedRoute>
        ),
        errorElement: <ErrorPage />,
        children: [
            {
                index: true,
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.DASHBOARD}>
                        <DashboardOverviewRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "users",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.USERS}>
                        <UsersRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "riders",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.RIDERS}>
                        <RidersRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "riders/:userId",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.RIDERS}>
                        <RiderDetailsRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "drivers",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.DRIVERS}>
                        <DriversRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "drivers/:driverId",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.DRIVERS}>
                        <DriverDetailsRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "admin-management",
                element: (
                    <PermissionRoute roles={["SUPER_ADMIN"]}>
                        <AdminsRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "admin-management/create",
                element: (
                    <PermissionRoute roles={["SUPER_ADMIN"]}>
                        <CreateAdminRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "admin-management/:adminId",
                element: (
                    <PermissionRoute roles={["SUPER_ADMIN"]}>
                        <AdminDetailsRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "admin-management/:adminId/edit",
                element: (
                    <PermissionRoute roles={["SUPER_ADMIN"]}>
                        <EditAdminRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "ride-bookings-trips",
                element: (
                    <PermissionRoute
                        anyOf={[
                            ...SECTION_PERMISSIONS.RIDE_OFFERS,
                            ...SECTION_PERMISSIONS.RIDE_BOOKINGS,
                            ...SECTION_PERMISSIONS.TRIPS,
                        ]}
                    >
                        <RideBookingsTripsLayout />
                    </PermissionRoute>
                ),
                children: [
                    {
                        path: "rides",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.RIDE_OFFERS}>
                                <RideOffersRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "rides/:rideOfferId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.RIDE_OFFERS}>
                                <RideOfferDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "bookings",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.RIDE_BOOKINGS}>
                                <BookingsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "bookings/:bookingId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.RIDE_BOOKINGS}>
                                <BookingDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "trips",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.TRIPS}>
                                <TripsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "trips/:tripId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.TRIPS}>
                                <TripDetailsRoute />
                            </PermissionRoute>
                        )
                    }
                ]
            },
            {
                path: "wallet-payments",
                element: (
                    <PermissionRoute
                        anyOf={[
                            ...SECTION_PERMISSIONS.WALLETS,
                            ...SECTION_PERMISSIONS.TRANSACTIONS,
                            ...SECTION_PERMISSIONS.PAYMENTS,
                            ...SECTION_PERMISSIONS.PLATFORM_WALLET,
                        ]}
                    >
                        <WalletPaymentsLayout />
                    </PermissionRoute>
                ),
                children: [
                    {
                        path: "wallets",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.WALLETS}>
                                <WalletsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "wallets/:walletId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.WALLETS}>
                                <WalletDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "transactions",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.TRANSACTIONS}>
                                <TransactionsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "transactions/:transactionId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.TRANSACTIONS}>
                                <TransactionDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "payments",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.PAYMENTS}>
                                <PaymentsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "payments/:paymentId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.PAYMENTS}>
                                <PaymentDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "platform",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.PLATFORM_WALLET}>
                                <PlatformWalletRoute />
                            </PermissionRoute>
                        )
                    }
                ]
            },
            {
                path: "approval-queue",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.APPROVALS}>
                        <ApprovalQueueRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "approval-queue/:approvalId",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.APPROVALS}>
                        <ApprovalQueueDetailsRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "approvals",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.APPROVALS}>
                        <ApprovalsLayout />
                    </PermissionRoute>
                ),
                children: [
                    {
                        path: "queue",
                        element: <Navigate to="/approval-queue" replace />
                    },
                    {
                        path: "queue/:approvalId",
                        element: <Navigate to="/approval-queue" replace />
                    },
                    {
                        path: "driver-licences",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.APPROVALS}>
                                <DriverLicenceRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "driver-licences/:verificationId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.APPROVALS}>
                                <DriverLicenceDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "road-worthiness",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.APPROVALS}>
                                <RoadWorthinessRoute />
                            </PermissionRoute>
                        )
                    }
                ]
            },
            {
                path: "fraud-safety",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.FRAUD}>
                        <FraudSafetyLayout />
                    </PermissionRoute>
                ),
                children: [
                    {
                        path: "alerts",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.FRAUD}>
                                <AlertsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "alerts/:alertId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.FRAUD}>
                                <AlertDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "cases",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.FRAUD}>
                                <CasesRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "cases/:caseIdOrNumber",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.FRAUD}>
                                <CaseDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "overview",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.FRAUD}>
                                <FraudOverviewRoute />
                            </PermissionRoute>
                        )
                    }
                ]
            },
            {
                path: "live-monitoring",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.LIVE_MONITORING}>
                        <LiveMonitoringLayout />
                    </PermissionRoute>
                ),
                children: [
                    {
                        path: "dashboard",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.LIVE_MONITORING}>
                                <LiveDashboardRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "drivers",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.LIVE_MONITORING}>
                                <LiveDriversRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "drivers/:driverId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.LIVE_MONITORING}>
                                <LiveDriverDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "trips",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.LIVE_MONITORING}>
                                <LiveTripsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "trips/:tripId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.LIVE_MONITORING}>
                                <LiveTripDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "analytics",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.LIVE_MONITORING}>
                                <LiveAnalyticsRoute />
                            </PermissionRoute>
                        )
                    }
                ]
            },
            {
                path: "notifications",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.MESSAGING}>
                        <NotificationsLayout />
                    </PermissionRoute>
                ),
                children: [
                    {
                        path: "announcements",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.MESSAGING}>
                                <AnnouncementsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "announcements/new",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.MESSAGING_MANAGE}>
                                <AnnouncementComposerRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "announcements/:announcementId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.MESSAGING}>
                                <AnnouncementDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "stats",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.MESSAGING}>
                                <MessagingStatsRoute />
                            </PermissionRoute>
                        )
                    }
                ]
            },
            {
                path: "reports-analytics",
                element: (
                    <PermissionRoute anyOf={SECTION_PERMISSIONS.ANALYTICS}>
                        <ReportsAnalyticsLayout />
                    </PermissionRoute>
                ),
                children: [
                    {
                        path: "summary",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.ANALYTICS}>
                                <AnalyticsSummaryRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "financial",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.ANALYTICS}>
                                <FinancialAnalyticsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "revenue",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.ANALYTICS}>
                                <RevenueAnalyticsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "trips",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.ANALYTICS}>
                                <TripAnalyticsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "users",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.ANALYTICS}>
                                <UserAnalyticsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "geographic",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.ANALYTICS}>
                                <GeographicAnalyticsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "operational",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.ANALYTICS}>
                                <OperationalAnalyticsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "quality",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.ANALYTICS}>
                                <QualityAnalyticsRoute />
                            </PermissionRoute>
                        )
                    }
                ]
            },
            {
                path: "audit-logs",
                element: (
                    <PermissionRoute
                        anyOf={[
                            ...SECTION_PERMISSIONS.AUDIT,
                            ...SECTION_PERMISSIONS.AUDIT_SECURITY,
                        ]}
                    >
                        <AuditLogsLayout />
                    </PermissionRoute>
                ),
                children: [
                    {
                        path: "logs",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.AUDIT}>
                                <AuditLogsListRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "logs/:logId",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.AUDIT}>
                                <AuditLogDetailsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "security",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.AUDIT_SECURITY}>
                                <SecurityAuditRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "statistics",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.AUDIT}>
                                <AuditStatsRoute />
                            </PermissionRoute>
                        )
                    },
                    {
                        path: "my-actions",
                        element: (
                            <PermissionRoute anyOf={SECTION_PERMISSIONS.AUDIT}>
                                <MyActionsRoute />
                            </PermissionRoute>
                        )
                    }
                ]
            },
            {
                path: "platform-configuration",
                element: (
                    <PermissionRoute roles={["SUPER_ADMIN"]}>
                        <PlatformConfigurationRoute />
                    </PermissionRoute>
                )
            },
            {
                path: "settings",
                element: <SettingsRoute />
            }
        ]
    }
]);
